import { useContentfulLiveUpdates } from '@contentful/live-preview/react';
import { Box, Typography } from '@mui/material';
import { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import { useSearchParams } from 'next/navigation';
import { useRouter as useNextRouter } from 'next/router';
import { useEffect, useState } from 'react';

import { dataLayerConfig, NextPageHead, useAppContext } from '@shared-components-web';
import { getFirebaseConfigProps } from '@src/auth-firebase/with-firebase-config';
import MainSkeleton from '@src/components/MainSkeleton';
import withStoreReset from '@src/hoc/withStoreReset';
import useIsLoggedIn from '@src/hooks/useIsLoggedIn';
import {
	AppConfigByAppNameDocument,
	AppMetadataByAppNameDocument,
	LandingPageByPathDocument,
	PageFragment,
} from '@src/lib/__generated/contentful/sdk';
import { type LocationAttribute } from '@src/lib/__generated/vetspire/sdk';
import FetchAppointmentTypes from '@src/services/fetch-appointment-types';
import { getBffFacilitiesScheduleType } from '@src/services/getBffFacilitiesScheduleType';
import { getBffNearByFacilitiesByLocation } from '@src/services/getBffNearByFacilitiesByLocation';
import {
	useAppointmentSelectionStore,
	useClientInformationStore,
	useFacilityStore,
	usePatientInformationStore,
	useRouteHandlerStore,
	useTimeSlotStore,
} from '@src/store';
import useReasonForVisitStore, { ReasonForVisitType } from '@src/store/modules/reason-for-visit.store';
import { buildIntelligentSchedulingPayload } from '@src/utils/build-intelligent-scheduling-payload';
import checkFacilitySupport from '@src/utils/check-facility-support';
import { checkSlotAvailability } from '@src/utils/check-slot-availability';
import DetermineIndexing from '@src/utils/determine-indexing';
import DetermineScheduleType from '@src/utils/determine-schedule-type';
import getConfigServices from '@src/utils/get-config-services';
import getFacilityId from '@src/utils/get-facility-id';
import { getRootLevelConfig } from '@src/utils/get-root-level-config';
import { getServerSideTranslations } from '@src/utils/get-serverside-translations';
import getUserCoordinates from '@src/utils/get-user-coordinates';
import { mapVisitReasonToBeffeTypes } from '@src/utils/map-appointment-types-to-beffe';
import resolveConfigData from '@src/utils/resolve-config-data';

const HomePage = (props: InferGetServerSidePropsType<typeof getServerSideProps>) => {
	const { brazeSdkKey, user, env, preview, domain } = props;
	const router = useNextRouter();
	const searchParams = useSearchParams();
	const { environment, config } = useAppContext();
	const facilityCode = searchParams?.get('fc');
	const timeSlot = searchParams?.get('timeSlot');
	const preselectedReasonForVisit = searchParams?.get('visitReason');
	const { featureFlags, logo } = props.config as BrandThemeConfig;
	const pageData = useContentfulLiveUpdates(props.page);
	const { name, path, title, indexed, description, canonical, breadcrumbsCollection, ogImage } = pageData;

	const { facility, scheduleType, allAppointmentTypes, setFacility, setScheduleType, setAllAppointmentTypes } =
		useFacilityStore((store) => store);

	const { setTimeSlot, setTimeSlotFacility, setTimeSlotTaken } = useTimeSlotStore((store) => store);
	const { routesInitialized, initializeRoutes, advanceToFirstIncompleteRoute } = useRouteHandlerStore(
		(store) => store
	);
	const { setFromPAO, sessionGuid } = useAppointmentSelectionStore((store) => store);
	const { clientId, newClient } = useClientInformationStore((store) => store);
	const { species, sex, neutered, birthDate, isEstimatedAge, breed, patientId, notes, notesReasonForVisit } =
		usePatientInformationStore((store) => store);
	const { checked, isLoggedIn } = useIsLoggedIn();
	const [schedulingSupported, setSchedulingSupported] = useState(false);
	const [supportCheckComplete, setSupportCheckComplete] = useState(false);

	const { setMainReasonForVisit } = useReasonForVisitStore((store) => store);

	useEffect(() => {
		// Tracking schedule type for facilities
		const beffeGraphQlUrl =
			environment === 'prod' ? config.services.prod.BEFFE_GRAPHQL_URL : config.services.nonprod.BEFFE_GRAPHQL_URL;

		getBffFacilitiesScheduleType(config?.bffBrandType, beffeGraphQlUrl)
			.then((response) => {
				// remove type name from the response
				const scheduleTypes =
					response.data?.facilitiesScheduleType?.map((facility) => ({
						id: facility.id,
						scheduleType: facility.scheduleType,
					})) ?? [];

				dataLayerConfig({
					event: 'schedule_type_for_facilities',
					scheduleTypes: scheduleTypes?.length > 0 ? JSON.stringify(scheduleTypes) : undefined,
				});
			})
			.catch((error) => {
				console.error('Error fetching facilities schedule type:', error);
			});
	}, []);

	useEffect(() => {
		if (
			!supportCheckComplete &&
			!facilityCode &&
			!facility?.id &&
			checked &&
			user?.coordinates?.latitude !== undefined &&
			user?.coordinates?.longitude !== undefined
		) {
			const queryByUserLocation = async () => {
				try {
					const beffeGraphQlUrl =
						environment === 'prod'
							? config.services.prod.BEFFE_GRAPHQL_URL
							: config.services.nonprod.BEFFE_GRAPHQL_URL;
					const latitude = user.coordinates.latitude;
					const longitude = user.coordinates.longitude;

					const response = await getBffNearByFacilitiesByLocation(
						config.bffBrandType,
						latitude,
						longitude,
						beffeGraphQlUrl,
						// setting a default radius so that we can get a facility if they are outside our facilities local range
						3000
					);

					const fetchedFacility = response.data?.facilityByLocation?.[0];

					if (!fetchedFacility?.code) {
						throw new Error('No facility found for the given coordinates.');
					}

					// BFF returns a facility, so we need to fetch support and Location information
					const facilitySupport = await checkFacilitySupport(
						fetchedFacility?.code,
						config.bffBrandType,
						beffeGraphQlUrl
					);
					setSchedulingSupported(facilitySupport.supported);

					if (facilitySupport?.location) {
						setFacility(facilitySupport.location);

						const { attributes } = facilitySupport.location;

						if (attributes) {
							const facilityScheduleType = DetermineScheduleType(attributes as LocationAttribute[]);
							setScheduleType(facilityScheduleType);

							const { scheduleSupportedAppointmentTypes } = await FetchAppointmentTypes();
							setAllAppointmentTypes(scheduleSupportedAppointmentTypes);
							initializeRoutes(isLoggedIn ? 'existing' : 'new');
						}
					} else {
						throw new Error('No location found.');
					}
				} catch (error) {
					console.error('Error fetching facility by user location:', error);
					setSchedulingSupported(false);
				}
			};

			queryByUserLocation();
		}
	}, [checked, facility, facilityCode, initializeRoutes, supportCheckComplete, isLoggedIn]);

	useEffect(() => {
		const facilityId = getFacilityId(facilityCode ?? null) ?? facility?.id ?? null;
		if (!checked) {
			return;
		}
		if (supportCheckComplete || !facilityId) {
			return;
		}

		const handleNavigate = () => {
			if (facility?.id === facilityId) {
				const setStoredFacility = async () => {
					setSchedulingSupported(true);
					initializeRoutes(isLoggedIn ? 'existing' : 'new');
					setSupportCheckComplete(true);
				};
				return void setStoredFacility();
			}

			if (facilityId) {
				const verifyFacilitySupport = async (id: string) => {
					try {
						const beffeGraphQlUrl =
							environment === 'prod'
								? config.services.prod.BEFFE_GRAPHQL_URL
								: config.services.nonprod.BEFFE_GRAPHQL_URL;
						const response = await checkFacilitySupport(id, config.bffBrandType, beffeGraphQlUrl);
						setSchedulingSupported(response.supported);

						if (response?.location) {
							setFacility(response.location);

							const { attributes } = response.location;

							if (attributes) {
								const facilityScheduleType = DetermineScheduleType(attributes as LocationAttribute[]);
								setScheduleType(facilityScheduleType);

								const { scheduleSupportedAppointmentTypes } = await FetchAppointmentTypes();
								setAllAppointmentTypes(scheduleSupportedAppointmentTypes);
								initializeRoutes(isLoggedIn ? 'existing' : 'new');
							}
						}
					} catch (error) {
						console.error('Facility verification failed', error);
						setFacility(null);
					} finally {
						setSupportCheckComplete(true);
					}
				};

				return void verifyFacilitySupport((facilityCode ?? facility?.id) as string);
			}
		};

		const handleTimeSlotChange = async () => {
			const [appointmentDate, appointmentTime] = timeSlot ? timeSlot.split('T') : [null, null];

			if (!appointmentDate || !appointmentTime) {
				handleNavigate();
				return;
			}

			const beffeAppointmentTypes = mapVisitReasonToBeffeTypes(preselectedReasonForVisit);
			const isPayload =
				facility && beffeAppointmentTypes
					? buildIntelligentSchedulingPayload({
							facility: facility as any,
							appointmentTypes: beffeAppointmentTypes,
							sessionGuid,
							clientId,
							patientId,
							mainFacilityId: null,
							newClient,
							species,
							sex,
							neutered,
							birthDate,
							isEstimatedAge,
							breed,
							mainReasonForVisit: null,
							notesReasonForVisit,
							notes,
					  })
					: { intelligentSchedulingEnabled: false, intelligentSchedulingPayload: null };

			// Set PAO intent before the availability check so the confirmation banner
			// renders on client-registration regardless of whether the specific slot is
			// still open at this moment.
			setFromPAO(true);
			setTimeSlot({
				date: appointmentDate,
				time: appointmentTime,
				__typename: 'AvailableTimeSlot',
				id: null,
				scheduleId: null,
			});

			try {
				const { scheduleSupportedAppointmentTypes } = await FetchAppointmentTypes();
				const availabilityChecks = await Promise.all(
					(scheduleSupportedAppointmentTypes ?? []).map(({ id }) =>
						checkSlotAvailability(
							parsedFacilityId,
							id,
							appointmentDate || '',
							appointmentTime || '',
							beffeAppointmentTypes,
							isPayload
						)
					)
				);

				// If all appointment types do not have an available slot, we will prompt the user to pick another time
				if (availabilityChecks.every((available) => !available)) {
					setTimeSlotTaken(true);
					handleNavigate();
					return;
				}

				const logRocket = globalThis?.LogRocket;
				if (logRocket && typeof logRocket.track === 'function' && preselectedReasonForVisit) {
					logRocket.track('AppointmentFromDirectTimePAO', {
						date: appointmentDate,
						time: appointmentTime,
						facility: facility?.id,
					});
				}

				setTimeSlotFacility(facility);
				handleNavigate();
			} catch (error) {
				console.error('Error checking slot availability:', error);
				handleNavigate();
			}
		};

		const setVisitReason = (reason) => {
			if (reason === 'sick') {
				setMainReasonForVisit(ReasonForVisitType.SickNeeds);
			} else if (reason === 'checkup') {
				setMainReasonForVisit(ReasonForVisitType.RoutineCheckUp);
			}
		};

		const parsedFacilityId = Number.parseInt(facilityId || '', 10);
		const hasAppointmentParams = parsedFacilityId && timeSlot;

		if (preselectedReasonForVisit) setVisitReason(preselectedReasonForVisit);

		if (hasAppointmentParams) {
			handleTimeSlotChange();
		} else {
			handleNavigate();
		}
	}, [
		allAppointmentTypes,
		facility,
		facilityCode,
		scheduleType,
		supportCheckComplete,
		timeSlot,
		preselectedReasonForVisit,
		setFacility,
		initializeRoutes,
		setScheduleType,
		setAllAppointmentTypes,
		setTimeSlot,
		setTimeSlotFacility,
		setTimeSlotTaken,
		setMainReasonForVisit,
		setFromPAO,
		checked,
	]);

	useEffect(() => {
		const utmMedium = searchParams?.get('utm_medium');

		import('@src/utils/braze-exports').then(({ initialize, openSession, logCustomEvent }) => {
			initialize(brazeSdkKey, {
				baseUrl: 'sdk.iad-07.braze.com',
				enableLogging: env?.ENVIRONMENT_NAME !== 'prod',
			});
			openSession();

			// Custom event drives the Canvas entry trigger (instead of "Start Session") so
			// UTM data is available for trigger filtering, e.g. excluding utm_medium=cpc.
			logCustomEvent('Page_Viewed', utmMedium ? { utm_medium: utmMedium } : undefined);
		});
	}, [brazeSdkKey, env, searchParams]);

	useEffect(() => {
		if (routesInitialized && schedulingSupported) {
			advanceToFirstIncompleteRoute((route) => router.push(route));
		}
	}, [routesInitialized, router, schedulingSupported, advanceToFirstIncompleteRoute]);

	const renderBody = () => {
		if (supportCheckComplete && !schedulingSupported) {
			return (
				<Box
					textAlign="center"
					pt={2}
					sx={{
						minHeight: '30rem',
					}}
				>
					<Typography>
						There&apos;s a problem on our end and online scheduling is currently unavailable. Please check
						back later.
					</Typography>
				</Box>
			);
		}
		return <MainSkeleton />;
	};

	/**
	 * Note:
	 *
	 * show this loader while we're doing the facility support check, and if the schedule
	 * type is open then keep it up so user doens't see the first form before they are
	 * automatically navigated to appointment-selection.
	 */
	return (
		<>
			<NextPageHead
				name={name}
				path={path}
				title={title}
				indexed={DetermineIndexing(indexed, props.env, preview, featureFlags.doNotIndexWholeSite, domain)}
				description={description}
				canonical={canonical}
				breadcrumbs={breadcrumbsCollection?.items}
				originUrl={process.env.NEXT_PUBLIC_SITE_URL}
				ogImage={ogImage?.desktopImage?.image?.cdn_url ?? logo.dark}
			/>
			{renderBody()}
		</>
	);
};

export const getServerSideProps: GetServerSideProps = async ({ req, locale, preview = false }) => {
	const { brandConfig, logger, gqlClient } = getRootLevelConfig({ req, preview, pageName: 'home' });
	try {
		const appName = brandConfig.contentfulAppId || process.env.TAG_APP_NAME || 'TAG_Commercial_Web_Sandbox';
		const [appPagesData, appMetadata] = await Promise.all([
			gqlClient.query(AppConfigByAppNameDocument, { appName, preview }).toPromise(),
			gqlClient.query(AppMetadataByAppNameDocument, { appName, preview }).toPromise(),
		]);

		if (appPagesData.error) {
			logger.error(`CONTENTFUL_ERROR_APP_PAGES_DATA: ${appPagesData.error}`);
		}

		if (appMetadata.error) {
			logger.error(`CONTENTFUL_ERROR_APP_META_DATA: ${appMetadata.error}`);
		}

		const appData = {
			...appPagesData.data?.appCollection?.items[0],
			...appMetadata.data?.appCollection?.items[0],
		};

		const pageData = await gqlClient
			.query(LandingPageByPathDocument, {
				appName,
				path: '/book-an-appointment/',
				preview,
			})
			.toPromise();

		if (pageData.error) {
			logger.error(`CONTENTFUL_ERROR_LANDING_PAGE_DATA: ${pageData.error}`);
		}

		const page = pageData.data?.pageCollection?.items?.[0] as PageFragment;

		const env = { ENVIRONMENT_NAME: process.env.ENVIRONMENT_NAME };
		// replace [env] with environment name for locationBarFindOfficeUrl
		// TODO: create a brand variable context to replace those variables from CMS in future
		if (appData?.header?.locationBarFindOfficeUrl) {
			const brandSubdomain = env?.ENVIRONMENT_NAME === 'prod' ? 'www' : 'wwwstg';
			appData.header.locationBarFindOfficeUrl = appData.header?.locationBarFindOfficeUrl?.replace(
				'[env]',
				brandSubdomain
			);
		}
		if (!appData) {
			throw new Error('App data not found');
		}
		logger.info(`/book-an-appointment/`);

		const transformedServices = getConfigServices(req, brandConfig);
		const firebaseProps = await getFirebaseConfigProps();
		return {
			props: {
				...(await getServerSideTranslations(locale)),
				page: {
					...(page || null),
				},
				env,
				config: resolveConfigData(brandConfig, appData, transformedServices),
				preview,
				appData,
				footerData: appData?.footer,
				brazeSdkKey: process.env[`${brandConfig.themeId === 'lovet' ? 'LOVET_' : ''}BRAZE_SDK_KEY`],
				user: getUserCoordinates(req),
				firebaseConfig: firebaseProps.props.firebaseConfig,
			},
		};
	} catch (e) {
		logger.error(`HOME_PAGE_LOAD_FAILURE: ${e}`);
		const env = { ENVIRONMENT_NAME: process.env.ENVIRONMENT_NAME };
		const firebaseProps = await getFirebaseConfigProps();
		return {
			props: {
				...(await getServerSideTranslations(locale)),
				env,
				config: brandConfig || { features: {}, services: { prod: {}, nonprod: {} } },
				preview,
				appData: null,
				footerData: null,
				brazeSdkKey: process.env[`${brandConfig.themeId === 'lovet' ? 'LOVET_' : ''}BRAZE_SDK_KEY`],
				user: getUserCoordinates(req),
				firebaseConfig: firebaseProps.props.firebaseConfig,
			},
		};
	}
};

export default withStoreReset(HomePage);
